/**
 * Drop-in replacement for `<QueryClientProvider>` that adds IndexedDB-backed
 * cache persistence with stale-while-revalidate restore-gating.
 *
 * `PersistQueryClientProvider` provides the QueryClient context itself (it wraps
 * one) plus an "is restoring" context that pauses query fetches until the
 * persisted cache has been hydrated - so the first paint uses cached rows
 * instead of a loading spinner, with no mount-time double-fetch.
 *
 * It is mounted ONCE and never remounted: partitioning by identity (user / org /
 * partner) is handled inside the storage adapter, which resolves the partition
 * key live per read/write. This deliberately avoids remounting the whole app on
 * login/logout/switch (which would interrupt the post-login navigation and reset
 * app state). The QueryClient is a stable module singleton; its in-memory cache
 * is cleared by the existing `queryClient.clear()` calls at those transitions.
 */

import type { ReactNode } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { buildPersistOptions } from './setup'

interface QueryPersistenceProviderProps {
  client: QueryClient
  children: ReactNode
}

// Built once (stable reference) so the provider never re-initialises persistence.
const persistOptions = buildPersistOptions()

export function QueryPersistenceProvider({
  client,
  children,
}: QueryPersistenceProviderProps) {
  return (
    <PersistQueryClientProvider client={client} persistOptions={persistOptions}>
      {children}
    </PersistQueryClientProvider>
  )
}
